import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useLocation } from 'react-router-dom';
import * as identity from './identity/identityContract';
import { useCanBuildWorkflows } from './identity/useIsAdmin';
import { AuthProvider } from './auth/AuthContext';
import RequireAuth from './auth/RequireAuth';
import Login from './pages/Login';
import AcceptInvitePage from './pages/AcceptInvitePage';
import UserMenu from './components/UserMenu';
import TenantSwitcher from './components/TenantSwitcher';
import FeatureRequestButton from './components/FeatureRequestButton';
import ProductNotEnabled from './components/ProductNotEnabled';
import BuildStaleBanner from './components/BuildStaleBanner';
import UploadPage from './components/UploadPage';
import DocumentViewer from './libraryViewer/DocumentViewer';
import TemplateStudio from './pages/TemplateStudio';
import TemplateBuilderPage from './pages/TemplateBuilderPage';
import TemplateSettingsPage from './pages/TemplateSettingsPage';
import FieldLibrarySettingsPage from './pages/FieldLibrarySettingsPage';
import CaptureSettingsPage from './pages/CaptureSettingsPage';
import DocumentTypesSettingsPage from './pages/DocumentTypesSettingsPage';
import DepartmentsSettingsPage from './pages/DepartmentsSettingsPage';
import VendorProfilesPage from './pages/VendorProfilesPage';
import VendorDirectoryPage from './pages/VendorDirectoryPage';
import VendorImportPage from './pages/VendorImportPage';
import DestinationsPage from './pages/DestinationsPage';
import DocumentSourcesPage from './pages/DocumentSourcesPage';
import WorkflowsPage from './pages/WorkflowsPage';
import WorkflowDesigner from './pages/WorkflowDesigner';
import ProcessingMonitorPage from './pages/ProcessingMonitorPage';
import ExtractionInsightsPage from './pages/ExtractionInsightsPage';
import ReviewDetailPage from './pages/ReviewDetailPage';
import SplitReviewPage from './pages/SplitReviewPage';
import AccountSettings from './pages/AccountSettings';
import UsersSettings from './pages/UsersSettings';
import ErrorBoundary from './components/ErrorBoundary';
import './index.css';

// 2026-06-14 power_user unit: route-guard the workflow builder so a plain member
// can't deep-link it (the real client gate; nav/button hiding is affordance). The
// server still enforces (buildOnly + the PUT draft gate). undefined = whoAmI not
// yet resolved → show the RequireAuth loading treatment rather than bounce a
// builder mid-load. (2026-08-23: this branch used to `return null`, which
// blanked the page for the whoAmI round trip on any direct nav or refresh of a
// gated route.)
function RequireBuilder({ children }) {
  const canBuild = useCanBuildWorkflows();
  if (canBuild === undefined) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <p style={{ color: '#64748b', fontSize: '1rem' }}>Loading…</p>
      </div>
    );
  }
  if (!canBuild) return <Navigate to="/" replace />;
  return children;
}

// Exported for App.navigation.test.jsx.
export function Navigation() {
  const location = useLocation();
  // 2026-06-14 power_user unit: workflow authoring nav is for builders
  // (admin || power_user); plain members don't see Workflows. Server enforces.
  // (isAdmin moved to UserMenu with the Users link — nav-IA regroup.)
  const [canBuild, setCanBuild] = useState(false);
  useEffect(() => {
    let active = true;
    identity.whoAmI().then((r) => {
      if (active && r.ok) {
        setCanBuild(['admin', 'power_user'].includes(r.data.role));
      }
    });
    return () => { active = false; };
  }, []);

  return (
    <header className="header">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h1><img src="/steliad_logo_white.png" alt="Steliad" style={{ height: 32, width: 'auto', display: 'block' }} /></h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <FeatureRequestButton />
          <TenantSwitcher />
          <UserMenu />
        </div>
      </div>
      <nav>
        <Link
          to="/"
          className={location.pathname === '/' ? 'active' : ''}
        >
          Upload & Process
        </Link>
        <Link
          to="/processing"
          className={(location.pathname.startsWith('/processing') || location.pathname.startsWith('/review')) ? 'active' : ''}
        >
          Processing
        </Link>
        {canBuild && (
          <Link
            to="/workflows"
            className={location.pathname.startsWith('/workflows') ? 'active' : ''}
          >
            Workflows
          </Link>
        )}
        {/* Configuration ▾ — the single home for the document model (vault
            decision 2026-07-14-capture-ui-navigation-ia). Nav-only regroup:
            every route path and its gating is unchanged. Vendor Profiles moved
            IN from top level; Account/Users/Departments moved OUT to UserMenu. */}
        <div className="nav-dropdown">
          <button className="nav-dropdown-trigger">
            Configuration ▾
          </button>
          <div className="nav-dropdown-menu">
            {canBuild && (
              <Link
                to="/settings/document-types"
                className={location.pathname === '/settings/document-types' ? 'active' : ''}
              >
                Document Types
              </Link>
            )}
            {canBuild && (
              <Link
                to="/settings/field-library"
                className={location.pathname === '/settings/field-library' ? 'active' : ''}
              >
                Index Fields
              </Link>
            )}
            {canBuild && (
              <Link
                to="/template-studio"
                className={(location.pathname.startsWith('/template-studio') || location.pathname.startsWith('/template-builder')) ? 'active' : ''}
              >
                Template Studio
              </Link>
            )}
            {/* Vendor Directory — the tenant's own vendor records. Visible to
                everyone; the page hides every edit behind useIsAdmin. Not
                '/vendors/…': that prefix would also highlight Vendor Profiles. */}
            <Link
              to="/vendor-directory"
              className={location.pathname.startsWith('/vendor-directory') ? 'active' : ''}
            >
              Vendor Directory
            </Link>
            <Link
              to="/vendors"
              className={location.pathname.startsWith('/vendors') ? 'active' : ''}
            >
              Vendor Profiles
            </Link>
            <Link
              to="/destinations"
              className={location.pathname === '/destinations' ? 'active' : ''}
            >
              Destinations
            </Link>
            {/* Document Sources — cloud ingestion channels. NOT gated on
                canBuild: the list, folder and health are member-visible by
                ruling (seeing that documents arrive from /Scans - YSoft is not
                the same as browsing the connecting admin's OneDrive), and the
                page hides every mutation behind useIsAdmin. */}
            <Link
              to="/settings/ingestion"
              className={location.pathname === '/settings/ingestion' ? 'active' : ''}
            >
              Document Sources
            </Link>
            <Link
              to="/settings/capture"
              className={location.pathname === '/settings/capture' ? 'active' : ''}
            >
              Capture Settings
            </Link>
            <Link
              to="/extraction-insights"
              className={location.pathname.startsWith('/extraction-insights') ? 'active' : ''}
            >
              Extraction Insights
            </Link>
          </div>
        </div>
      </nav>
    </header>
  );
}

function ProtectedLayout() {
  return (
    <RequireAuth>
      {/* LB-14: renders null until capture-service returns
          403 product_not_enabled, then takes over the page. Inside RequireAuth
          because the gate only applies to an authenticated tenant. */}
      <ProductNotEnabled />
      <div className="app">
        <Navigation />
        {/* Renders null until this tab is known to be running an older bundle
            than the deploy. Inside the Router (it reads useLocation to re-check
            on navigation) and above <main> so it pushes content down rather than
            covering it. No dismiss, by design — see the component. */}
        <BuildStaleBanner />
        <main className="main">
          <ErrorBoundary>
          <Routes>
            <Route path="/" element={<UploadPage />} />
            <Route path="/viewer/:documentId" element={<DocumentViewer />} />
            {/* Processing Monitor — real-time document pipeline visibility */}
            <Route path="/processing" element={<ProcessingMonitorPage />} />
            {/* Redirect /queue to /processing for bookmarks */}
            <Route path="/queue" element={<Navigate to="/processing" replace />} />
            {/* Review viewer — unified detail page for any queued document */}
            <Route path="/review/:executionId" element={<ReviewDetailPage />} />
            {/* /documents/:id is the View target on the Completed tab.
                ReviewDetailPage's loadData already handles a docId in the URL
                (the workflow-execution lookup falls through to its
                document-direct fallback). The component hides Approve / Reject
                / Flag when documentStatus is 'published' so this becomes a
                read-only viewer while still allowing per-field corrections
                and re-extract. */}
            <Route path="/documents/:executionId" element={<ReviewDetailPage />} />
            {/* Slice 3b: verify an already-split container. Its own page, not
                ReviewDetailPage — whose action bar would offer Reject (which
                overwrites a container's status), Re-extract and Studio. */}
            <Route path="/split-review/:documentId" element={<SplitReviewPage />} />
            <Route path="/extraction-insights" element={<ExtractionInsightsPage />} />
            <Route path="/template-studio" element={<RequireBuilder><TemplateStudio /></RequireBuilder>} />
            <Route path="/template-builder" element={<RequireBuilder><TemplateBuilderPage /></RequireBuilder>} />
            <Route path="/template-settings/:templateId" element={<RequireBuilder><TemplateSettingsPage /></RequireBuilder>} />
            <Route path="/destinations" element={<DestinationsPage />} />
            <Route path="/workflows" element={<RequireBuilder><WorkflowsPage /></RequireBuilder>} />
            <Route path="/workflows/:id/designer" element={<RequireBuilder><WorkflowDesigner /></RequireBuilder>} />
            <Route path="/settings/field-library" element={<RequireBuilder><FieldLibrarySettingsPage /></RequireBuilder>} />
            <Route path="/settings/capture" element={<CaptureSettingsPage />} />
            {/* Fixed by INGESTION_CONNECTION_UI_URL — capture-service redirects
                the browser here after an OAuth callback. No RequireBuilder:
                power_user is a builder, not an admin, and this page's gate is
                useIsAdmin on the controls rather than on the route. */}
            <Route path="/settings/ingestion" element={<DocumentSourcesPage />} />
            <Route path="/settings/document-types" element={<RequireBuilder><DocumentTypesSettingsPage /></RequireBuilder>} />
            <Route path="/settings/departments" element={<RequireBuilder><DepartmentsSettingsPage /></RequireBuilder>} />
            <Route path="/settings/account" element={<AccountSettings />} />
            <Route path="/settings/users" element={<UsersSettings />} />
            <Route path="/vendors" element={<VendorProfilesPage />} />
            <Route path="/vendor-directory" element={<VendorDirectoryPage />} />
            <Route path="/vendor-directory/import" element={<VendorImportPage />} />
          </Routes>
          </ErrorBoundary>
        </main>
      </div>
    </RequireAuth>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <Router>
          <Routes>
            <Route path="/login" element={<Login />} />
            {/* Public: invited users have no Capture session yet, so this must
                sit OUTSIDE ProtectedLayout/RequireAuth. The accept flow mints
                the session on this (Capture) origin, then lands on '/'. */}
            <Route path="/invites/accept" element={<AcceptInvitePage />} />
            <Route path="/*" element={<ProtectedLayout />} />
          </Routes>
        </Router>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
